"use client"

/**
 * Panel "Xuất bản" của Letáky — cùng khuôn với panel của Post
 * (`post/pages/Publish.tsx`): trạng thái hiện tại → khung hiển thị → thao tác → ngôn ngữ.
 *
 * Letak không có workflow duyệt như Post, nên trạng thái suy ra từ các mốc:
 *   publishedAt  → Đã xuất bản
 *   scheduledAt  → Đã lên lịch (cron Strapi sẽ đăng)
 *   còn lại      → Nháp
 * Các thao tác ở đây ghi thẳng các mốc (không lưu nội dung form) — nút "Cập nhật"
 * ở action bar mới lưu nội dung.
 */

import { describeDisplayWindow } from "@/(admin)/components/publish/display-window"
import { DisplayWindowMode, DisplayWindowModal } from "@/(admin)/components/publish/display-window-modal"
import { LanguagePublish } from "@/(admin)/modules/post/pages/StatusPublish"
import { useCheckPermission } from "@/hook/use-check-permission"
import { t } from "@/languages/i18n"
import type { PostWorkflowStatus } from "@/types/types"
import { buildPostStatusDisplay } from "@/utils/post-status-display"
import { ClockCircleOutlined, ExclamationCircleFilled, StopOutlined } from "@ant-design/icons"
import { App, Button, Space, Tag, Typography } from "antd"
import React, { useState } from "react"

const { Text } = Typography

export interface LetakyVisibility {
  publishedAt: string | null
  scheduledAt: string | null
  unpublishAt: string | null
}

interface LocalizationEntry {
  id: number
  locale: string
}

interface LetakyPublishPanelProps {
  isEditMode: boolean
  visibility: LetakyVisibility
  loading?: boolean
  onPublishNow: (unpublishAt: string | null) => void
  onSchedule: (scheduledAt: string, unpublishAt: string | null) => void
  /** unpublishAt = null để bỏ lịch gỡ. */
  onSaveUnpublishAt: (unpublishAt: string | null) => void
  /** Về nháp: xóa publishedAt + cả 2 lịch. */
  onUnpublish: () => void
  // Gợi ý nhanh 2 mốc từ dateStart / dateEnd của letak.
  dateStart?: string | null
  dateEnd?: string | null
  language: string
  setLanguages: (language: string) => void
  localizations?: LocalizationEntry[]
  onNavigateToLocale?: (id: number) => void
  onCreateLocale?: (targetLocale: string) => void
}

export const getLetakyStatus = ({ publishedAt, scheduledAt }: LetakyVisibility): PostWorkflowStatus =>
  publishedAt ? "published" : scheduledAt ? "scheduled" : "draft"

const LetakyPublishPanel: React.FC<LetakyPublishPanelProps> = ({
  isEditMode,
  visibility,
  loading = false,
  onPublishNow,
  onSchedule,
  onSaveUnpublishAt,
  onUnpublish,
  dateStart,
  dateEnd,
  language,
  setLanguages,
  localizations = [],
  onNavigateToLocale,
  onCreateLocale,
}) => {
  const { modal } = App.useApp()
  const { hasPermission } = useCheckPermission()
  const [windowMode, setWindowMode] = useState<DisplayWindowMode | null>(null)

  // Letak chỉ có quyền CRUD; xuất bản = update các mốc.
  const canUpdate = hasPermission("letak", "update")

  const status = getLetakyStatus(visibility)
  const isScheduled = status === "scheduled"
  const isLiveOrScheduled = status !== "draft"
  const statusItems = buildPostStatusDisplay({ status }, t)

  const hasWindowInfo = isScheduled || (status === "published" && visibility.unpublishAt)
  const hasActions = isEditMode && canUpdate

  const handleConfirmUnpublish = () => {
    modal.confirm({
      title: isScheduled ? t("Xác nhận bỏ lịch đăng") : t("Xác nhận hủy xuất bản"),
      icon: <ExclamationCircleFilled />,
      content: isScheduled
        ? t("Letak sẽ quay về bản nháp, lịch đăng và lịch gỡ (nếu có) sẽ bị xóa.")
        : t("Bạn có chắc chắn muốn hủy xuất bản letak này? Letak sẽ quay trở lại trạng thái bản nháp."),
      okText: t("Xác nhận"),
      okType: "danger",
      cancelText: t("Hủy"),
      onOk: onUnpublish,
    })
  }

  return (
    <Space direction="vertical" className="w-full" size={10}>
      <div className="flex flex-col gap-2">
        <div className="flex justify-between items-center border-b border-gray-100 pb-2">
          <Text className="text-sm font-medium" style={{ color: "var(--admin-foreground)" }}>
            {t("Trạng thái hiện tại")}
          </Text>
          <div className="flex gap-1">
            {statusItems.map(item => (
              <Tag key={item.label} color={item.tone} className="mr-0">
                {item.label}
              </Tag>
            ))}
          </div>
        </div>

        {hasWindowInfo && (
          <div className="flex flex-col gap-1 bg-gray-50 rounded-md px-3 py-2 border border-gray-100 text-xs text-gray-500">
            <span>
              {describeDisplayWindow({
                start: isScheduled ? visibility.scheduledAt : null,
                end: visibility.unpublishAt,
              })}
            </span>
          </div>
        )}

        {hasActions && (
          <div className="bg-gray-50 p-3 rounded-lg border border-gray-100 mt-2">
            <div className="flex flex-wrap gap-2">
              {status === "draft" && (
                <Button
                  block
                  type="primary"
                  onClick={() => setWindowMode("publish")}
                  loading={loading}
                  className="flex items-center justify-center"
                >
                  {t("Xuất bản")}
                </Button>
              )}

              {isScheduled && (
                <Button
                  block
                  icon={<ClockCircleOutlined />}
                  onClick={() => setWindowMode("edit")}
                  loading={loading}
                  className="flex items-center justify-center"
                >
                  {t("Sửa thời gian hiển thị")}
                </Button>
              )}

              {status === "published" && (
                <Button
                  block
                  icon={<ClockCircleOutlined />}
                  onClick={() => setWindowMode("end")}
                  loading={loading}
                  className="flex items-center justify-center"
                >
                  {visibility.unpublishAt ? t("Sửa lịch gỡ bài") : t("Lên lịch gỡ bài")}
                </Button>
              )}

              {isLiveOrScheduled && (
                <Button
                  block
                  danger
                  icon={<StopOutlined />}
                  onClick={handleConfirmUnpublish}
                  loading={loading}
                  className="flex items-center justify-center"
                >
                  {isScheduled ? t("Bỏ lịch đăng") : t("Hủy xuất bản")}
                </Button>
              )}
            </div>
          </div>
        )}
      </div>

      <DisplayWindowModal
        mode={windowMode}
        onClose={() => setWindowMode(null)}
        current={{ start: isScheduled ? visibility.scheduledAt : null, end: visibility.unpublishAt }}
        canPublish={canUpdate}
        canSchedule={canUpdate}
        loading={loading}
        publishTitle={t("Xuất bản letak")}
        onPublishNow={onPublishNow}
        onSchedule={onSchedule}
        onSaveEnd={onSaveUnpublishAt}
        startSuggestion={dateStart ? { label: t("Dùng ngày bắt đầu"), value: dateStart } : null}
        endSuggestion={dateEnd ? { label: t("Dùng ngày kết thúc"), value: dateEnd } : null}
      />

      <LanguagePublish
        language={language}
        setLanguages={setLanguages}
        isCreateEdit={!isEditMode}
        localizations={localizations}
        onNavigateToLocale={onNavigateToLocale}
        onCreateLocale={onCreateLocale}
      />
    </Space>
  )
}

export default LetakyPublishPanel
